
import React, { useMemo, useState, useCallback } from "react";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import DateRangeFilter from "components/DateRangeFilter/DateRangeFilter";
import AgGridWrapper from "components/AgGrid/AgGridWrapper";
import GridPagination from "components/CustomPagination/GridPagination";
import useReportGrid from "Hooks/UseReportGrid";
import ExcelDownloadButton from "components/ExcelDownLoadButton/ExcelDownloadButton";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import { exportToExcel } from "Utils/ExcelExport";

const col = (headerName, field, filter = "agTextColumnFilter", extra = {}) => ({
  headerName, field, filter, ...extra,
});

const NUMBER = "agNumberColumnFilter";
const RIGHT  = { cellStyle: { textAlign: "right" } };
const PCT    = { valueFormatter: (p) => p.value != null ? `${p.value}` : "" };


const COLUMN_DEFS = [
  col("Outlet Code",        "outletCode",      undefined,   { minWidth: 140 }),
  col("Store Name",         "storeName",       undefined,   { minWidth: 160 }),
  col("Store Type",         "storeType",       undefined,   { minWidth: 80, width: 120 }),
  col("Total Score",        "totalScore",      NUMBER, { minWidth: 80, width: 110, ...RIGHT }),
  col("Availability Score", "avbScr",          NUMBER, { minWidth: 80, width: 130, ...RIGHT }),
  col("Activation Score",   "actScr",          NUMBER, { minWidth: 80, width: 120, ...RIGHT }),
  col("Total Cooler Score", "totalCoolerScr",  NUMBER, { minWidth: 80, width: 130, ...RIGHT }),
  col("Total Purity %",     "totalPurityPct",  NUMBER, { minWidth: 80, width: 110, ...RIGHT, ...PCT }),
  col("No. of Shelves",     "numberOfShelves", NUMBER, { minWidth: 80, width: 110, ...RIGHT }),
  ...[1,2,3,4,5,6,7,8,9,10].map((n) =>
    col(`Pure Shelf ${n} %`, `purShelf${n}Pct`, NUMBER, { minWidth: 80, width: 110, ...RIGHT, ...PCT })
  ),
];

// ── Excel config ──────────────────────────────────────────────────────────────
const EXCEL_HEADERS = [
  "Outlet Code", "Store Name", "Store Type",
  "Total Score", "Availability Score", "Activation Score",
  "Total Cooler Score", "Total Purity %", "No. of Shelves",
  ...[1,2,3,4,5,6,7,8,9,10].map((n) => `Pure Shelf ${n} %`),
];

const EXCEL_FIELDS = [
  "outletCode", "storeName", "storeType",
  "totalScore", "avbScr", "actScr",
  "totalCoolerScr", "totalPurityPct", "numberOfShelves",
  ...[1,2,3,4,5,6,7,8,9,10].map((n) => `purShelf${n}Pct`),
];


// ── API fetch fn (passed into hook) ──────────────────────────────────────────
const fetchStoreAbstract = async ({ from, to, page, size }) => {
  const res = await apiClient.post(
    `${API_CONFIG.ENDPOINTS.STORE_ABSTRACT.REPORT}?page=${page}&size=${size}`,
    {
      from: { date: from.day, month: from.month, year: from.year },
      to:   { date: to.day,   month: to.month,   year: to.year   },
    }
  );
  return res.data;
};

// ── Row mapper (passed into hook) ─────────────────────────────────────────────
const mapRow = (item) => ({
  outletCode:      item.storeid           || "",
  storeName:       item.storeName         || "",
  storeType:       item.storeType         || "",
  totalScore:      item.totalScore        || 0,
  avbScr:          item.availabilityScore || 0,
  actScr:          item.activationScore   || 0,
  totalCoolerScr:  item.coolerScore       || 0,
  totalPurityPct:  item.totalPurity       || 0,
  numberOfShelves: item.totalNoOfShelves  || 0,
  ...Object.fromEntries(
    [1,2,3,4,5,6,7,8,9,10].map((n) => [`purShelf${n}Pct`, item[`shelf${n}Pur`] || 0])
  ),
});

function StoreAbstract(){
  const columnDefs=useMemo(()=>COLUMN_DEFS,[])

  const {
    fromDate, toDate, handleFromDateChange, handleToDateChange,
    committedDates,
    rowData, loading,
    currentPage, totalPages, totalElements, pageSize,
    handlePrevious, handleNext,
    gridApi, filteredCount, onGridReady, onFilterChanged,
  } = useReportGrid(fetchStoreAbstract, mapRow, 10);

   // ── Verify filter (local, no re-fetch needed) ─────────────────────────────
  const [verifyFilter, setVerifyFilter] = useState("total");
 
  const filteredRowData = useMemo(() => {
    if (verifyFilter === "verified")   return rowData.filter((r) => r.isVerified === true);
    if (verifyFilter === "unverified") return rowData.filter((r) => r.isVerified === false);
    return rowData;
  }, [rowData, verifyFilter]);


   // ── Excel download ─────────────────────────────────────────────────────────
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadExcel = useCallback(async () => {
    setDownloadLoading(true);
    try {
      // ✅ Fetch ALL records in one shot using totalElements as page size.
      // This bypasses the current page — so you always get the full dataset.
      const fetchSize = totalElements > 0 ? totalElements : 100000;
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.STORE_ABSTRACT.REPORT}?page=0&size=${fetchSize}`,
        {
          from: { date: committedDates.from.split("-")[2], month: committedDates.from.split("-")[1], year: committedDates.from.split("-")[0] },
          to:   { date: committedDates.to.split("-")[2],   month: committedDates.to.split("-")[1],   year: committedDates.to.split("-")[0]   },
        }
      );

      const allRows = (res.data?.content ?? []).map(mapRow);

      let exportData = allRows;
      if (gridApi) {
        const filterModel = gridApi.getFilterModel();
        const isFilterActive = Object.keys(filterModel).length > 0;
        if (isFilterActive) {
          exportData = allRows;
        }
      }

      exportToExcel({
        headers:   EXCEL_HEADERS,
        fields:    EXCEL_FIELDS,
        data:      exportData,
        sheetName: "Store Abstract Report",
        fileName:  `Store_Abstract_Report_${committedDates.from}_to_${committedDates.to}`,
      });

    } catch (err) {
      console.error("Excel download failed:", err);
      alert("Failed to download Excel. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  }, [gridApi, committedDates, totalElements]);

 return(
   <Box minH="100vh" bg="gray.50" pt={{ base: "10vh", md: "13vh" }}>
     <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        gap={4} mb={6} px={6}
      >
        <Box>
          <Heading size="lg" color="gray.800" mb={2}>Store Abstract Report</Heading>
          <Text color="gray.500">Drill into store performance metrics and trends</Text>
        </Box>
 
        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
        />
      </Flex>

      {/* Record Count + Verify Filters + Download */}
      <Flex
        align="center" justify="space-between"
        mb={{ base: 3, md: 4 }} px={6}
        flexWrap="wrap" gap={3}
      >
        <Flex align="center" gap={4} flexWrap="wrap">
          {/* Record badge */}
          <Flex
            align="center" gap={1.5}
            bg="gray.700"
            px={{ base: 3, md: 4 }} py={{ base: 1, md: 1.5 }}
            borderRadius="full"
            boxShadow="0 2px 4px rgba(0,0,0,0.2)"
          >
            <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} fontWeight="bold" color="white">
              {filteredCount < totalElements ? "Filtered:" : "Total:"}
            </Text>
            <Text color="white" fontSize={{ base: "sm", sm: "md", md: "lg" }} fontWeight="bold">
              {filteredCount} Records
            </Text>
          </Flex>
 
          {/* Verify radio buttons */}
          <Flex align="center" gap={4}>
            {["total", "verified", "unverified"].map((option) => (
              <Flex
                key={option}
                align="center" gap={1.5}
                cursor="pointer"
                onClick={() => setVerifyFilter(option)}
              >
                <Box
                  w="18px" h="18px" borderRadius="full"
                  border="2px solid"
                  borderColor={verifyFilter === option ? "red.500" : "gray.400"}
                  display="flex" alignItems="center" justifyContent="center"
                  transition="all 0.2s"
                >
                  {verifyFilter === option && (
                    <Box w="9px" h="9px" borderRadius="full" bg="red.500" />
                  )}
                </Box>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight={verifyFilter === option ? "bold" : "medium"}
                  color={verifyFilter === option ? "red.500" : "gray.800"}
                  textTransform="capitalize"
                  userSelect="none"
                >
                  {option}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
 
        <ExcelDownloadButton
          onClick={handleDownloadExcel}
          loading={downloadLoading}
          disabled={rowData.length === 0}
        />
      </Flex>

       <Box px={3} pb={8}>
        <AgGridWrapper
          rowData={filteredRowData}
          columnDefs={columnDefs}
          loading={loading}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
        />
      </Box>

      {/* Pagination */}
      <GridPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />



   </Box>
 )
}

export default StoreAbstract;